import fetch from 'app/utils/requests/fetch';

// Every mirror request is bounded. An un-timed one is worse here than a failed one: the replay queue is
// serial, so a single hung request stalls every later mutation in the page session, and a stalled job never
// reaches the reporter — Sentry would show silence, which is indistinguishable from healthy. A half-open
// connection to an API being drained or decommissioned is the normal shape of the window this code exists
// for, so the timeout is the difference between a reported failure and a silent stop.
export const MIRROR_REQUEST_TIMEOUT_MS = 10_000;

export class MirrorTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`hyrox-mirror: request timed out after ${timeoutMs}ms`);
    this.name = 'MirrorTimeoutError';
  }
}

// An explicit controller rather than AbortSignal.timeout(): the timer is cleared on the way out, so a
// settled request leaves nothing pending behind it.
export const withTimeout = async <TResult>(
  timeoutMs: number,
  run: (signal: AbortSignal) => Promise<TResult>,
): Promise<TResult> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await run(controller.signal);
  } catch (error: unknown) {
    throw controller.signal.aborted ? new MirrorTimeoutError(timeoutMs) : error;
  } finally {
    clearTimeout(timer);
  }
};

// How hyrox-api tells a mirror replay apart from a write by a stale, never-reloaded pre-cutover bundle:
// every mirror request carries this GraphQL request extension, and hyrox-api records every mutation
// that arrives without it (its `stale_client_writes` table) for replay into one-fiit. It travels in the
// body rather than as a header on purpose — CORS in front of hyrox-api is owned by Istio, and an unlisted
// header would fail the browser preflight and silently break the mirror.
export const HYROX_MIRROR_EXTENSION = 'hyroxMirror';
export const HYROX_MIRROR_EXTENSIONS: Readonly<Record<string, string>> = { [HYROX_MIRROR_EXTENSION]: 'hyrox-app' };

export interface GraphQLResponse<TData> {
  data?: TData | null;
  errors?: Array<{ message: string; extensions?: { code?: string } }>;
}

export interface GraphQLRequest {
  url: string;
  query: string;
  variables?: Record<string, unknown>;
  operationName?: string;
  headers?: Record<string, string>;
  keepalive?: boolean;
  timeoutMs?: number;
}

interface PostGraphQLArgs extends GraphQLRequest {
  authorization: string;
}

export const postGraphQL = <TData>({
  url,
  authorization,
  query,
  variables = {},
  operationName,
  headers = {},
  keepalive = false,
  timeoutMs = MIRROR_REQUEST_TIMEOUT_MS,
}: PostGraphQLArgs): Promise<GraphQLResponse<TData>> =>
  withTimeout(timeoutMs, async (signal) => {
    const { body } = await fetch(url, {
      body: JSON.stringify({ extensions: HYROX_MIRROR_EXTENSIONS, operationName, query, variables }),
      headers: {
        authorization,
        'content-type': 'application/json',
        ...headers,
      },
      keepalive,
      method: 'POST',
      signal,
    });

    return body as GraphQLResponse<TData>;
  });
